import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export default function AuthPage({ onNavigate, redirect = 'home', defaultUserType = 'voyageur' }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('voyageur');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailSent] = useState(false);

  useEffect(() => {
    if (defaultUserType && defaultUserType !== 'voyageur') {
      setUserType(defaultUserType);
      setMode('register');
    }
  }, [defaultUserType]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasCodeInUrl = params.has('code');
    const codeFromSession = sessionStorage.getItem('referral_code_pending');
    if (codeFromSession && !hasCodeInUrl) {
      const redirectTime = sessionStorage.getItem('referral_redirect_time');
      const isRecent = redirectTime && (Date.now() - parseInt(redirectTime)) < 5000;
      if (isRecent) {
        setReferralCode(codeFromSession);
        setMode('register');
      }
      sessionStorage.removeItem('referral_code_pending');
      sessionStorage.removeItem('referral_redirect_time');
    }
  }, []);

  const handleSubmit = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      if (mode === 'login') {
        const { data: loginData, error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        if (loginData?.user) {
          const { data: prof } = await supabase.from('host_profiles').select('is_manager').eq('id', loginData.user.id).single();
          if (prof?.is_manager) {
            localStorage.setItem('havenly_is_manager', 'true');
            onNavigate('dashboard'); return;
          } else {
            localStorage.setItem('havenly_is_manager', 'false');
          }
        }
        onNavigate(redirect);
      } else {
        const { data, error: err } = await supabase.auth.signUp({
          email, password,
          options: { data: { full_name: name, user_type: userType } }
        });
        if (err) throw err;
// Alerte admin nouvelle inscription
await fetch('/api/send-email', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    type: 'new_host',
    to: 'contact@havenly.fr',
    data: {
      name: name || email.split('@')[0],
      email: email,
      date: new Date().toLocaleDateString('fr-FR'),
    }
  })
}).catch(() => {});

        if (data?.user) {
          let referrerId = null;
          if (referralCode) {
            const { data: referrer } = await supabase.from('host_profiles').select('id').eq('referral_code', referralCode.toUpperCase()).single();
            if (referrer) referrerId = referrer.id;
          }
          const firstName = name.split(' ')[0].toUpperCase().replace(/[^A-Z]/g, '').slice(0, 8);
          const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
          const newReferralCode = `${firstName}-HAVENLY-${suffix}`;

          await supabase.from('host_profiles').upsert({
            id: data.user.id,
            full_name: name,
            email: email,
            status: 'pending',
            level: 'havenly',
            is_manager: userType === 'conciergerie',
            commission_rate: 0.08, // 8% à vie pour les 50 premiers hôtes — passer à 0.12 après 3 mois pour les suivants
            referral_discount_expires_at: referrerId ? new Date(Date.now() + 90*24*60*60*1000).toISOString() : null,
            referral_code: newReferralCode,
            referral_credits: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }, { onConflict: 'id' });

          if (referrerId) {
            await supabase.from('referrals').insert({ referrer_id: referrerId, referred_id: data.user.id, status: 'pending', credit_amount: 30, created_at: new Date().toISOString() });
          }
        }

        try {
          await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'welcome', to: email, data: { name: name || 'là' } }) });
        } catch (e) {}

        try {
          await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'new_host', to: 'contact@havenly.fr', data: { name: name || 'Inconnu', email, userType, date: new Date().toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }), referralCode: referralCode || null, commission: referralCode ? '10% (filleul parrainé)' : '12% (standard)' } }) });
        } catch (e) {}

        try {
          await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'create_profile', data: { userId: data.user.id, name, email, userType } }) });
        } catch (e) {}

        localStorage.setItem('havenly_pending_user_type', userType);
        if (userType === 'conciergerie') {
          localStorage.setItem('havenly_is_manager', 'true');
          onNavigate('newlisting');
        } else {
          localStorage.setItem('havenly_is_manager', 'false');
          if (userType === 'hote') onNavigate('newlisting');
          else onNavigate('home');
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
  if (referralCode) sessionStorage.setItem('referral_code_pending', referralCode);
  localStorage.setItem('pending_user_type', userType); // localStorage + bonne clé
  await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin, queryParams: { prompt: 'select_account' } } });
};

  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'var(--sans)', outline: 'none', color: 'var(--ink)', transition: 'border-color 0.15s', boxSizing: 'border-box' };

  if (emailSent) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', paddingTop: 80 }}>
      <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 20, padding: '2.5rem', width: '100%', maxWidth: 420, textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 24, letterSpacing: '-0.5px', marginBottom: 12 }}>Confirmez votre email</h2>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 8 }}>Un lien de confirmation a été envoyé à</p>
        <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', marginBottom: 24 }}>{email}</p>
        <span onClick={() => onNavigate('home')} style={{ fontSize: 13, color: 'var(--ink-3)', cursor: 'pointer', textDecoration: 'underline' }}>Retour à l'accueil</span>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', paddingTop: 80 }}>
      <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 20, padding: '2.5rem', width: '100%', maxWidth: 420 }}>
        <div onClick={() => onNavigate('home')} style={{ marginBottom: 28, cursor: 'pointer', textAlign: 'center' }}>
          <svg viewBox="0 0 210 52" style={{ height: 28, width: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="0" y="44" fontFamily="Georgia,serif" fontSize="52" fontWeight="400" fontStyle="italic" letterSpacing="-2" fill="#1A1A18">haven<tspan fill="#1D9E75" dx="-3">ly</tspan></text>
          </svg>
        </div>

        {referralCode && (
          <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green-mid)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--green-mid)' }}>Invitation parrainage</div>
              <div style={{ fontSize: 12, color: 'var(--green-mid)', opacity: 0.8 }}>Code : {referralCode} · 10% de commission pendant 3 mois</div>
            </div>
          </div>
        )}

        <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, letterSpacing: '-0.5px', marginBottom: 6, textAlign: 'center' }}>
          {mode === 'login' ? 'Bon retour !' : 'Créer un compte'}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', textAlign: 'center', marginBottom: 28 }}>
          {mode === 'login' ? 'Connectez-vous pour accéder à vos réservations' : 'Rejoignez Havenly en quelques secondes'}
        </p>

        {error && <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#A32D2D' }}>{error}</div>}

        {mode === 'register' && (
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 480 ? '1fr' : '1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
            {[
              { type: 'voyageur', icon: '✈️', label: 'Je voyage', desc: 'Trouver un logement', color: '#534AB7', bg: '#EEEDFE', border: '#AFA9EC' },
              { type: 'hote', icon: '🏡', label: 'Hote', desc: 'Je loue mon bien', color: '#0F6E56', bg: '#E8F5F0', border: '#1D9E75' },
              { type: 'conciergerie', icon: '🏢', label: 'Conciergerie', desc: 'Gérer plusieurs biens', color: '#854F0B', bg: '#FDF0E6', border: '#E8A87C' },
            ].map(t => {
              const selected = userType === t.type;
              return (
                <button key={t.type} onClick={() => setUserType(t.type)} style={{ padding: '14px 8px', border: `2px solid ${selected ? t.border : 'var(--border)'}`, borderRadius: 14, fontFamily: 'var(--sans)', cursor: 'pointer', background: selected ? t.bg : 'var(--white)', textAlign: 'center', transition: 'all 0.15s', boxShadow: selected ? `0 0 0 3px ${t.border}22` : 'none' }}>
                  <div style={{ fontSize: 24, marginBottom: 5 }}>{t.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: selected ? t.color : 'var(--ink)', marginBottom: 2 }}>{t.label}</div>
                  <div style={{ fontSize: 10, color: selected ? t.color : 'var(--ink-4)', lineHeight: 1.4 }}>{t.desc}</div>
                  {selected && <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 3, background: t.border, color: '#fff', fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}><svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>Sélectionné</div>}
                </button>
              );
            })}
          </div>
        )}

        {mode === 'register' && userType === 'hote' && (
          <div style={{ background: '#0F1A16', border: '1px solid #1D9E75', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 16 }}>⭐</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1D9E75' }}>Hôte Fondateur — 8% de commission</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>8% à vie pour les 50 premiers hôtes fondateurs</div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mode === 'register' && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Prénom et nom</label>
              <input type="text" placeholder="Marie Dupont" value={name} onChange={e => setName(e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Adresse email</label>
            <input type="email" placeholder="marie@exemple.fr" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Mot de passe</label>
            <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>
          {mode === 'register' && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Code parrainage <span style={{ fontWeight: 400 }}>(optionnel)</span></label>
              <input type="text" placeholder="Ex: MARIE-1234" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: 1 }} onFocus={e => e.target.style.borderColor = 'var(--green)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
          )}
        </div>

        {mode === 'login' && (
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <span onClick={async () => { if (!email) { setError('Entrez votre email ci-dessus.'); return; } await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin }); setError(null); alert(`Email envoyé à ${email}`); }} style={{ fontSize: 12, color: 'var(--green)', cursor: 'pointer' }}>Mot de passe oublié ?</span>
          </div>
        )}

        <button onClick={handleSubmit} disabled={loading} style={{ width: '100%', padding: '12px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)', marginTop: 20, opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Chargement...' : mode === 'login' ? 'Se connecter' : 'Créer mon compte'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: '0.5px', background: 'var(--border)' }} />
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>ou</span>
          <div style={{ flex: 1, height: '0.5px', background: 'var(--border)' }} />
        </div>

        <button onClick={handleGoogle} style={{ width: '100%', padding: '11px', background: 'var(--white)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--white)'}>
          <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/><path d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
          Continuer avec Google
        </button>

        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--ink-3)', marginTop: 24 }}>
          {mode === 'login' ? 'Pas encore de compte ? ' : 'Déjà un compte ? '}
          <span onClick={() => setMode(mode === 'login' ? 'register' : 'login')} style={{ color: 'var(--green)', fontWeight: 500, cursor: 'pointer' }}>
            {mode === 'login' ? "S'inscrire" : 'Se connecter'}
          </span>
        </p>
        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-4)', marginTop: 16, lineHeight: 1.6 }}>
          En continuant, vous acceptez nos <span onClick={() => onNavigate('legal', 'cgu')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>CGU</span> et notre <span onClick={() => onNavigate('legal', 'confidentialite')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>politique de confidentialité</span>
        </p>
      </div>
    </div>
  );
}
